import { memo, useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useHeaderCollapse } from "@/components/layout/private/main/components/Header/HeaderCollapseContext";

import SharedHeader, { type SharedHeaderProps } from "./SharedHeader";
import SharedSidebar, { type SharedSidebarProps } from "./SharedSidebar";
import { SharedMenuItem } from "src-next/shared/ui/menuTypes";

export interface SharedLayoutColapseProps {
  children: ReactNode;
  logo?: ReactNode;
  title?: string;
  menuItems: SharedMenuItem[];
  /** Header customizado. Quando omitido, usa SharedHeader. */
  HeaderComponent?: React.ComponentType<SharedHeaderProps>;
  /** Sidebar customizada. Quando omitida, usa SharedSidebar. */
  SidebarComponent?: React.ComponentType<SharedSidebarProps>;
  /**
   * Controle opcional do estado de expansão da navegação (sidebar).
   *
   * Quando fornecido, o layout opera em modo "controlled" e não gerencia
   * estado interno de expansão. Isso permite que aplicações legadas sincronizem
   * o estado com contexts externos (ex.: fechar sidebar ao navegar).
   */
  navigationExpanded?: boolean;
  onNavigationExpandedChange?: (expanded: boolean) => void;
}

/**
 * Isola o subtree principal de renderizações do layout.
 *
 * Motivação:
 * - Alternar o colapse (expanded/collapsed) deve rerenderizar header/sidebar,
 *   mas NÃO deve invalidar páginas inteiras. Ao envolver o slot principal em
 *   um componente memoizado que só depende de `children`, evitamos rerenders
 *   do conteúdo quando `children` mantém a mesma referência.
 */
const LayoutMainSlot = memo<{ children: ReactNode }>(({ children }) => {
  return <>{children}</>;
});

LayoutMainSlot.displayName = "LayoutMainSlot";

/**
 * Layout base compartilhado para aplicações autenticadas.
 *
 * - Header fixo + sidebar colapsável.
 * - Responsivo (desktop com menu permanente, mobile com drawer temporário).
 * - Não conhece detalhes de domínio; apenas recebe menu, logo e título.
 * - Cada aplicação decide se usa ou não este layout.
 */
const LayoutColapse = memo<SharedLayoutColapseProps>(
  ({
    children,
    logo,
    title,
    menuItems,
    HeaderComponent,
    SidebarComponent,
    navigationExpanded,
    onNavigationExpandedChange,
  }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const { mobileHeaderSpacerHeight } = useHeaderCollapse();

    const defaultHeaderSpacerHeight =
      typeof theme.mixins.toolbar.minHeight === "number"
        ? theme.mixins.toolbar.minHeight
        : 64;

    const contentTopInset = isDesktop
      ? "0px"
      : mobileHeaderSpacerHeight ?? `${defaultHeaderSpacerHeight}px`;

    const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] =
      useState(true);
    const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] =
      useState(false);

    const isNavigationExpanded =
      navigationExpanded !== undefined
        ? navigationExpanded
        : isDesktop
          ? isDesktopNavigationExpanded
          : isMobileNavigationExpanded;

    const setIsNavigationExpanded = useCallback(
      (expanded: boolean) => {
        if (onNavigationExpandedChange) {
          onNavigationExpandedChange(expanded);
          return;
        }
        if (isDesktop) {
          setIsDesktopNavigationExpanded(expanded);
        } else {
          setIsMobileNavigationExpanded(expanded);
        }
      },
      [isDesktop, onNavigationExpandedChange],
    );

    const handleToggleHeaderMenu = useCallback(
      (open: boolean) => {
        setIsNavigationExpanded(open);
      },
      [setIsNavigationExpanded],
    );

    const layoutRef = useRef<HTMLDivElement | null>(null);
    const EffectiveHeader = HeaderComponent ?? SharedHeader;
    const EffectiveSidebar = SidebarComponent ?? SharedSidebar;

    return (
      <Box
        ref={layoutRef}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: { xs: "100dvh", md: "100vh" },
          width: "100%",
          bgcolor: "#F4F6F8",
        }}
      >
        {/* Header fixo (AppBar) — fora da linha sidebar+conteúdo */}
        <EffectiveHeader
          logo={logo}
          title={title}
          menuOpen={isNavigationExpanded}
          onToggleMenu={handleToggleHeaderMenu}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            bgcolor: "#F4F6F8",
          }}
        >
          <EffectiveSidebar
            items={menuItems}
            expanded={isNavigationExpanded}
            setExpanded={setIsNavigationExpanded}
            container={layoutRef.current ?? undefined}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: "hidden",
              bgcolor: "#F4F6F8",
            }}
          >
            <Box
              component="main"
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorY: "contain",
                bgcolor: "transparent",
                px: { xs: 0.5, md: 2 },
                pt: { xs: 0.5, md: 2 },
                /* Espaço para a pill de navegação mobile flutuante */
                pb: {
                  xs: "calc(104px + env(safe-area-inset-bottom, 0px))",
                  md: 2,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 0 auto",
                  minHeight: "100%",
                  pt: isDesktop ? 0 : `calc(${contentTopInset} + 4px)`,
                  px: { xs: 0.5, md: 2 },
                  pb: {
                    xs: "calc(96px + env(safe-area-inset-bottom, 0px))",
                    md: 2,
                  },
                }}
              >
                <LayoutMainSlot>{children}</LayoutMainSlot>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

LayoutColapse.displayName = "SharedLayoutColapse";

export default LayoutColapse;
